import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { paymentSettingsService } from 'components/api/paymentSettings';
import { PaymentSetting } from 'types/paymentSetting';
import { Alert } from 'components/alert/alert';
import styles from './CreateEditPaymentSettingModal.module.scss';

interface CreateWithdrawMethodModalProps {
  open: boolean;
  siteId: string | number;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateWithdrawMethodModal: React.FC<CreateWithdrawMethodModalProps> = ({
  open,
  siteId,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settings, setSettings] = useState<PaymentSetting[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [paymentSettingId, setPaymentSettingId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  useEffect(() => {
    if (open && siteId) {
      fetchSettings();
    }
  }, [open, siteId]);

  const fetchSettings = async () => {
    setSettingsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('type', 'withdraw');
      params.append('type', 'both');

      const response = await paymentSettingsService.getSettings(siteId, params);
      const payload = response.data;

      let parsedData = [];
      if (Array.isArray(payload)) {
        parsedData = payload;
      } else if (payload?.data?.settings) {
        parsedData = payload.data.settings;
      } else if (payload?.data?.data) {
        parsedData = payload.data.data;
      } else if (payload?.data) {
        parsedData = Array.isArray(payload.data) ? payload.data : payload.data.docs || [];
      }

      // Safety filter on frontend to ensure only 'withdraw' and 'both' types are shown
      const filteredData = parsedData.filter(
        (s: PaymentSetting) =>
          s.type?.toLowerCase() === 'withdraw' || s.type?.toLowerCase() === 'both',
      );

      setSettings(filteredData);
    } catch (err: any) {
      setError('Failed to load payment settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentSettingId || !name) return;

    const selectedSetting = settings.find((s) => s.id === paymentSettingId);
    if (!selectedSetting) return;

    setLoading(true);
    setError(null);
    try {
      await paymentSettingsService.createWithdrawMethod({
        paymentSettingId: selectedSetting.id,
        code,
        name,
        imageUrl: null,
        isEnabled: false,
        sortOrder: 0,
      });
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create withdraw method');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPaymentSettingId('');
    setName('');
    setCode('');
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: styles.dialogPaper }}
      maxWidth="sm"
      fullWidth
    >
      <Typography variant="h6" className={styles.title}>
        Create Withdraw Method
      </Typography>

      {error && <Alert type="error" message={error} className={styles.modalAlert} />}

      <form onSubmit={handleSubmit} className={styles.form}>
        <TextField
          select
          fullWidth
          size="small"
          label="Payment Setting"
          value={paymentSettingId}
          onChange={(e) => setPaymentSettingId(Number(e.target.value))}
          className={styles.input}
          required
          disabled={loading || settingsLoading}
          InputProps={{
            endAdornment: settingsLoading ? <CircularProgress size={20} /> : null,
          }}
        >
          {settings?.map((setting) => (
            <MenuItem key={setting.id} value={setting.id}>
              {setting.paymentName} (ID: {setting.id})
            </MenuItem>
          ))}
          {settings?.length === 0 && !settingsLoading && (
            <MenuItem disabled>No payment settings available</MenuItem>
          )}
        </TextField>

        <TextField
          fullWidth
          size="small"
          label="Method Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={styles.input}
          required
          disabled={loading}
          placeholder="e.g. Manual withdrawal"
        />

        <TextField
          fullWidth
          size="small"
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className={styles.input}
          required
          disabled={loading}
          placeholder="e.g. manual"
        />

        <Box className={styles.actions}>
          <Button onClick={handleClose} className={styles.cancelBtn} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            className={styles.submitBtn}
            disabled={!paymentSettingId || !name || !code || loading || settingsLoading}
          >
            {loading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default CreateWithdrawMethodModal;
